// MT Returns API service (MTWHT-RET-01 / PNW-1765)
//
// All Returns-tab data flows go through the return-view API family plus the
// async download endpoints. Everything routes through the shared MT api-utils
// helpers (auth-token injection, response unwrapping, error handling). Country
// context is sent via the X-Country-Code header.

import {
    MT_RETURN_VIEW_CONFIG_API,
    MT_RETURN_VIEW_EDIT_API,
    MT_RETURN_VIEW_FILTER_DATA_API,
    MT_RETURN_VIEW_GENERATE_API,
    MT_RETURN_VIEW_GRID_API,
    MT_RETURN_VIEW_METADATA_API,
    MT_RETURN_VIEW_REFRESH_API,
    MT_RETURN_VIEW_VIEW_API,
    MT_RETURNS_DOWNLOAD_ASYNC_API,
} from '@/multi-territory/mt-services/mt-api-endpoints'
import {
    mtBuildRequestHeader,
    mtHandleErrorResponse,
    mtHandleRequestFailure,
    mtParseJsonResponse,
    mtUrlBuilder,
    type MTApiResponse,
} from '@/multi-territory/mt-services/mt-api-utils'
import type {
    MTReturnEditRequest,
    MTReturnGridRequest,
    MTReturnGridResponse,
    MTReturnMetadata,
    MTReturnViewConfig,
    MTReturnViewData,
    MTReturnViewGenerateBody,
    MTReturnViewGenerateResult,
} from '@/multi-territory/mt-types/mt-returns'

function buildReturnsHeaders(countryCode: string): Record<string, string> {
  return { 'X-Country-Code': countryCode }
}

// --- Return VIEW CONFIG: combined filters + columns (single call) ------------

/**
 * GET the combined Returns-tab configuration in ONE call - both the filter
 * definitions AND the grid column definitions for the given country (same API
 * drives filter options and metadata columns). Country context via X-Country-Code.
 */
export const fetchMTReturnViewConfig = async (
  countryCode: string
): Promise<MTApiResponse<MTReturnViewConfig>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_CONFIG_API)
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnViewConfig>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * GET the distinct dropdown values for the configured filters, keyed by
 * filterKey: { '<filterKey>': [values...] }. Optional dependent filters are
 * passed as query params (e.g. { entity_name: 'Acme' }) to narrow the results.
 */
export const fetchMTReturnFilterData = async (
  countryCode: string,
  dependents: Record<string, string> = {}
): Promise<MTApiResponse<Record<string, string[]>>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_FILTER_DATA_API, {}, dependents)
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<Record<string, string[]>>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST return-view/generate?form=<FORM> - creates/replaces a returns_tracking
 * row and runs config-driven generation. Returns { status, uuid, trackingStatus,
 * totalRecords } or { status:'FAILED', error }.
 */
export const generateMTReturnView = async (
  countryCode: string,
  form: string,
  body: MTReturnViewGenerateBody
): Promise<MTApiResponse<MTReturnViewGenerateResult>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_GENERATE_API, {}, { form })
  const options = mtBuildRequestHeader(
    'POST',
    body,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnViewGenerateResult>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST return-view/grid?page=&pageSize= - the paginated status grid of
 * returns_tracking rows. Body carries simple equality { filters } plus the full
 * { searchCOList } operator set. Response: { columns?, rows, page, pageSize, totalRecords }.
 */
export const fetchMTReturnGrid = async (
  countryCode: string,
  page: number,
  pageSize: number,
  body: MTReturnGridRequest
): Promise<MTApiResponse<MTReturnGridResponse>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_GRID_API, {}, { page, pageSize })
  const options = mtBuildRequestHeader(
    'POST',
    body,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnGridResponse>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * POST returns-view/refresh/{uuid} - regenerate an existing tracking row in
 * place. Same response shape as generate.
 */
export const refreshMTReturnView = async (
  countryCode: string,
  uuid: string
): Promise<MTApiResponse<MTReturnViewGenerateResult>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_REFRESH_API(uuid))
  const options = mtBuildRequestHeader(
    'POST',
    null,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnViewGenerateResult>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * GET return-view/metadata?formType=<FORM> - fetch the form/tab layout on View click.
 * formType is optional for now; pass when available.
 */
export const fetchMTReturnViewMetadata = async (
  countryCode: string,
  formType?: string
): Promise<MTApiResponse<MTReturnMetadata>> => {
  const params: Record<string, string> = {}
  if (formType) params['formType'] = formType
  const url = mtUrlBuilder(MT_RETURN_VIEW_METADATA_API, {}, params)
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnMetadata>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * GET return-view/view/{tab}/{uuid} - open a generated return's output for a
 * single tab (data only, no layout). `tab` is the tab_key from the metadata
 * layout (e.g. 'FORM' or 'RETURN_DETAILS'), so the form and the return-details
 * grid are fetched independently as the user switches tabs.
 *
 * The RETURN_DETAILS tab is paginated server-side (India parity): pass
 * `page`/`size` and the BE returns only that page's rows plus a total count.
 * The FORM tab ignores these params.
 */
export const fetchMTReturnView = async (
  rowId: string,
  countryCode: string,
  tab: string,
  pagination?: { page?: number; size?: number }
): Promise<MTApiResponse<MTReturnViewData>> => {
  const query: Record<string, string | number> = {}
  if (pagination?.page != null) query['page'] = pagination.page
  if (pagination?.size != null) query['size'] = pagination.size
  const url = mtUrlBuilder(MT_RETURN_VIEW_VIEW_API(tab, rowId), {}, query)
  const options = mtBuildRequestHeader(
    'GET',
    null,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<MTReturnViewData>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

/**
 * PUT return-view/edit/{tab}/{uuid} - save edits for a generated return. `tab`
 * is the tab_key (FORM / RETURN_DETAILS) and `uuid` is the return-generation row
 * id (same id used for view/refresh/download). Supports bulk edit: `body.rows`
 * can carry the single FORM row OR multiple RETURN_DETAILS line items in one
 * call, each as { uuid, values }.
 */
export const editMTReturnView = async (
  countryCode: string,
  tab: string,
  uuid: string,
  body: MTReturnEditRequest
): Promise<MTApiResponse<string>> => {
  const url = mtUrlBuilder(MT_RETURN_VIEW_EDIT_API(tab, uuid))
  const options = mtBuildRequestHeader(
    'PUT',
    body,
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<string>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}

// --- Async download (id-based, India/Prepare parity) -------------------------

/**
 * 7a. POST returns/download-async?form=<FORM> - enqueue an async download for a
 * generated tracking row. FE sends only { returnGenerationUuid }; the server
 * reads the stored query/params. Returns the allocated numeric downloadId.
 */
export const enqueueMTReturnDownload = async (
  countryCode: string,
  form: string,
  returnGenerationUuid: string
): Promise<MTApiResponse<number>> => {
  const url = mtUrlBuilder(MT_RETURNS_DOWNLOAD_ASYNC_API, {}, { form })
  const options = mtBuildRequestHeader(
    'POST',
    { returnGenerationUuid },
    buildReturnsHeaders(countryCode)
  )
  try {
    const response = await fetch(url, options)
    if (!response.ok) return await mtHandleRequestFailure(response)
    return await mtParseJsonResponse<number>(response)
  } catch (error) {
    return mtHandleErrorResponse(error)
  }
}
